// MARK://dhk8/realtime/ws-client
// Generic typed WebSocket client. Browser + Electron renderer + Node (with a
// global WebSocket polyfill). Port of sc-restaurant's `realtime-client`,
// stripped of @sc-restaurant/* deps so any DHK8 feature (KDS tickets, orders,
// inventory, floor, …) can reuse it.
//
// Protocol (server → client):
//   { type: 'welcome', userId, tenantId?, branchIds? }   handshake done
//   { type: 'ping', ts }                                 server heartbeat → reply 'pong'
//   { type: 'event', channel, envelope }                 a domain event on a channel
//   { type: 'subscribed' | 'unsubscribed' | 'error' }    acks (ignored)
// Protocol (client → server):
//   { type: 'subscribe', channels }                      | { type: 'unsubscribe', channels }
//   { type: 'pong', ts }                                 | { type: 'ping', ts }   (our heartbeat)
//
// Features: auth token in the connect URL query, exponential reconnect backoff,
// re-subscribe on reconnect, app-level heartbeat (detects half-open sockets),
// 'sc:rehydrate'-style reconnect notification via the `open` listener.
// ── Impl ───────────────────────────────────────────────────────────────────
const DEFAULTS = { autoReconnect: true, heartbeatMs: 25_000, minBackoffMs: 1_000, maxBackoffMs: 30_000 };
export class WsClient {
    url;
    token;
    autoReconnect;
    heartbeatMs;
    minBackoffMs;
    maxBackoffMs;
    ws = null;
    handlers = new Map();
    listeners = new Map();
    welcomeResolve = null;
    welcomeReject = null;
    backoff;
    reconnectTimer = null;
    heartbeatTimer = null;
    closedByUser = false;
    hadOpen = false;
    _userId = null;
    _tenantId = null;
    _branchIds = [];
    constructor(opts) {
        this.url = opts.url;
        this.token = opts.token;
        this.autoReconnect = opts.autoReconnect ?? DEFAULTS.autoReconnect;
        this.heartbeatMs = opts.heartbeatMs ?? DEFAULTS.heartbeatMs;
        this.minBackoffMs = opts.minBackoffMs ?? DEFAULTS.minBackoffMs;
        this.maxBackoffMs = opts.maxBackoffMs ?? DEFAULTS.maxBackoffMs;
        this.backoff = this.minBackoffMs;
    }
    get userId() { return this._userId; }
    get tenantId() { return this._tenantId; }
    get branchIds() { return this._branchIds; }
    /** `true` once a 'welcome' has been received and not yet closed. */
    get connected() {
        const Ctor = globalThis.WebSocket;
        return !!this.ws && !!Ctor && this.ws.readyState === Ctor.OPEN;
    }
    /** Subscribe to lifecycle events. Returns an unsubscribe fn. */
    on(event, cb) {
        let set = this.listeners.get(event);
        if (!set) {
            set = new Set();
            this.listeners.set(event, set);
        }
        set.add(cb);
        return () => set.delete(cb);
    }
    emit(event, info) {
        const set = this.listeners.get(event);
        if (!set)
            return;
        for (const cb of set) {
            try {
                cb(info);
            }
            catch (err) {
                if (event !== 'error')
                    console.error('[dhk8-realtime] listener error', err);
            }
        }
    }
    /** Open the socket; resolves once the 'welcome' handshake completes. */
    connect() {
        this.closedByUser = false;
        return new Promise((resolve, reject) => {
            this.welcomeResolve = resolve;
            this.welcomeReject = reject;
            this.openSocket();
        });
    }
    openSocket() {
        const Ctor = globalThis.WebSocket;
        if (!Ctor) {
            const err = new Error('WebSocket not available in this environment');
            this.welcomeReject?.(err);
            this.welcomeReject = null;
            this.welcomeResolve = null;
            return;
        }
        const sep = this.url.includes('?') ? '&' : '?';
        const fullUrl = `${this.url}${sep}token=${encodeURIComponent(this.token)}`;
        const ws = new Ctor(fullUrl);
        this.ws = ws;
        ws.onopen = () => { };
        ws.onmessage = (ev) => this.handleMessage(ev.data);
        ws.onclose = (ev) => {
            this.ws = null;
            this.stopHeartbeat();
            this.emit('close', ev);
            if (this.welcomeReject) {
                this.welcomeReject(new Error(`closed before welcome (${ev.code})`));
                this.welcomeReject = null;
                this.welcomeResolve = null;
            }
            if (!this.closedByUser && this.autoReconnect)
                this.scheduleReconnect();
        };
        ws.onerror = (ev) => { this.emit('error', ev); };
    }
    scheduleReconnect() {
        if (this.reconnectTimer)
            return;
        const delay = this.backoff;
        this.backoff = Math.min(this.backoff * 2, this.maxBackoffMs);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.openSocket();
        }, delay);
    }
    startHeartbeat() {
        if (this.heartbeatMs <= 0)
            return;
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            this.send({ type: 'ping', ts: Date.now() });
        }, this.heartbeatMs);
    }
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }
    handleMessage(raw) {
        if (typeof raw !== 'string')
            return;
        let msg;
        try {
            msg = JSON.parse(raw);
        }
        catch {
            return;
        }
        switch (msg.type) {
            case 'welcome': {
                this._userId = msg.userId ?? null;
                this._tenantId = msg.tenantId ?? null;
                this._branchIds = msg.branchIds ?? [];
                const wasReconnect = this.hadOpen;
                this.hadOpen = true;
                this.backoff = this.minBackoffMs;
                const pending = [...this.handlers.keys()];
                if (pending.length > 0)
                    this.send({ type: 'subscribe', channels: pending });
                this.startHeartbeat();
                this.emit('open', { reconnected: wasReconnect });
                this.welcomeResolve?.();
                this.welcomeResolve = null;
                this.welcomeReject = null;
                return;
            }
            case 'ping':
                this.send({ type: 'pong', ts: msg.ts });
                return;
            case 'event': {
                const set = this.handlers.get(msg.channel);
                if (!set)
                    return;
                for (const h of set) {
                    try {
                        h(msg.envelope);
                    }
                    catch (err) {
                        console.error('[dhk8-realtime] handler threw', err);
                    }
                }
                return;
            }
            case 'subscribed':
            case 'unsubscribed':
            case 'error':
                return;
        }
    }
    send(msg) {
        const ws = this.ws;
        const Ctor = globalThis.WebSocket;
        if (!ws || !Ctor || ws.readyState !== Ctor.OPEN)
            return;
        ws.send(JSON.stringify(msg));
    }
    /** Subscribe a handler to a channel. Returns an unsubscribe fn. */
    subscribe(channel, handler) {
        let set = this.handlers.get(channel);
        const isNew = !set;
        if (!set) {
            set = new Set();
            this.handlers.set(channel, set);
        }
        set.add(handler);
        if (isNew)
            this.send({ type: 'subscribe', channels: [channel] });
        return () => {
            const s = this.handlers.get(channel);
            if (!s)
                return;
            s.delete(handler);
            if (s.size === 0) {
                this.handlers.delete(channel);
                this.send({ type: 'unsubscribe', channels: [channel] });
            }
        };
    }
    /** Close the socket and stop reconnecting. */
    close() {
        this.closedByUser = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.stopHeartbeat();
        this.ws?.close();
        this.ws = null;
    }
}
//# sourceMappingURL=ws-client.js.map